import {
  useInfiniteQueryCalendarAppointments,
  useInfiniteQueryCalendarEventOccurrences,
} from "@gymflow/api";
import { PARAMETER_DATE_FORMAT_WITHOUT_TZ } from "@gymflow/common";
import moment from "moment-timezone";
import { useEffect, useMemo } from "react";

import { useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { CalendarFilters } from "../../organisms";
import {
  CalendarAppointment,
  CalendarEvent,
  CalendarOccurrence,
} from "./types";
import { CalendarMode } from "./useCalendarMode";

export function useCalendarEvents({
  intervalLoaded,
  modeSelected,
  filteredStaffIds,
  filters,
  enabled = true,
}: {
  enabled?: boolean;
  intervalLoaded?: { start: Date; end: Date };
  modeSelected: CalendarMode;
  filteredStaffIds?: string[];
  filters: CalendarFilters;
}) {
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const dateFrom =
    intervalLoaded?.start &&
    moment(intervalLoaded.start).format(PARAMETER_DATE_FORMAT_WITHOUT_TZ);
  const {
    hasNextPage: hasMoreOccurrences,
    data: occurrences,
    fetchNextPage: fetchNextOccurrences,
    isFetching: isFetchingOccurrences,
  } = useInfiniteQueryCalendarEventOccurrences(
    {
      api,
      tz: club.timezone,
      filters: {
        dateFrom,
        dateTo:
          intervalLoaded?.end &&
          moment(intervalLoaded.end).format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        limit: 50,
        includeBookedCounts: true,
        includeWaitingCounts: true,
        activityId: filters.classes?.map(({ value }) => value),
        activityCategoryId: filters.classCategory?.map(({ value }) => value),
        eventHostId: filteredStaffIds,
        facilityId: filters.facility?.map(({ value }) => value),
      },
    },
    { enabled },
  );

  useEffect(() => {
    if (hasMoreOccurrences) {
      fetchNextOccurrences();
    }
  }, [
    hasMoreOccurrences,
    occurrences?.pageParams.length,
    fetchNextOccurrences,
  ]);

  const {
    hasNextPage: hasMoreAppointments,
    data: appointments,
    fetchNextPage: fetchNextAppointmentsPage,
    isFetching: isFetchingAppointments,
    refetch: refetchAppointments,
  } = useInfiniteQueryCalendarAppointments(
    {
      api,
      tz: club.timezone,
      filters: {
        dateFrom,
        dateTo:
          intervalLoaded?.end &&
          moment(intervalLoaded.end).format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        limit: 50,
        appointmentHostId: filteredStaffIds,
        appointableId: filters.appointments?.map(({ value }) => value),
        appointableCategoryIds: filters.appointmentCategory?.map(
          ({ value }) => value,
        ),
        appointmentFacilityId: filters.facility?.map(({ value }) => value),
      },
    },
    { enabled },
  );

  useEffect(() => {
    if (hasMoreAppointments) {
      fetchNextAppointmentsPage();
    }
  }, [
    hasMoreAppointments,
    appointments?.pageParams.length,
    fetchNextAppointmentsPage,
  ]);

  const calendarEvents = useMemo(() => {
    const areAppointmentExclusiveFiltersSelected =
      filters?.appointmentCategory?.length || filters?.appointments?.length;
    const areClassExclusiveFiltersSelected =
      filters?.classes?.length || filters?.classCategory?.length;
    const mappedOccurrences: CalendarEvent[] =
      occurrences?.pages
        .flatMap((t) => t)
        .map((occurrence) => {
          let resourceId;
          if (modeSelected === "TRAINER") {
            resourceId = occurrence.hostStaffId;
          }
          if (modeSelected === "FACILITY") {
            resourceId = occurrence.facilityId;
          }
          return {
            id: `occurrence-${occurrence.eventOccurrenceId}`,
            occurrenceId: occurrence.eventOccurrenceId,
            title: occurrence.activityName,
            start: occurrence.startDate,
            end: occurrence.endDate,
            hostName: occurrence.hostName,
            hostId: occurrence.hostStaffId,
            type: "OCCURRENCE",
            capacity: occurrence?.capacity,
            waitListCapacity: occurrence?.waitListCapacity,
            bookedCount: occurrence?.bookedCount,
            waitingCount: occurrence?.waitingCount,
            resourceId,
          } as CalendarOccurrence;
        })
        .filter(() => {
          if (modeSelected === "APPOINTMENTS") {
            return false;
          }
          if (
            areAppointmentExclusiveFiltersSelected &&
            !areClassExclusiveFiltersSelected
          ) {
            return false;
          }
          return !filters.eventType || filters.eventType === "OCCURRENCE";
        }) || [];

    const mappedAppointments: CalendarEvent[] =
      appointments?.pages
        .flatMap((t) => t)
        .map((appointment) => {
          let resourceId;
          if (modeSelected === "TRAINER") {
            resourceId = appointment.hostStaffId;
          }
          if (modeSelected === "FACILITY") {
            resourceId = appointment.facilityId;
          }
          return {
            id: `appointment-${appointment.appointmentId}`,
            appointmentId: appointment.appointmentId,
            recurringAppointmentId: appointment.recurringAppointmentId,
            title: appointment.appointableName,
            start: appointment.startDate,
            end: appointment.endDate,
            hostName: appointment.hostName,
            hostId: appointment.hostStaffId,
            participantName: appointment.participantName,
            appointmentStatus: appointment.status,
            type: "APPOINTMENT",
            resourceId,
            paymentStatus: appointment.paymentStatus,
          } as CalendarAppointment;
        })
        .filter((appointment) => {
          if (modeSelected === "CLASSES") {
            return false;
          }
          if (
            areClassExclusiveFiltersSelected &&
            !areAppointmentExclusiveFiltersSelected
          ) {
            return false;
          }
          if (appointment.appointmentStatus === "CANCELLED") {
            return false;
          }
          return !filters.eventType || filters.eventType === "APPOINTMENT";
        }) || [];

    return mappedOccurrences.concat(mappedAppointments).map((calendarEvent) => {
      const startMoment = moment(
        calendarEvent.start,
        PARAMETER_DATE_FORMAT_WITHOUT_TZ,
      );
      const endMoment = moment(
        calendarEvent.end,
        PARAMETER_DATE_FORMAT_WITHOUT_TZ,
      );
      if (endMoment.isSameOrBefore(startMoment)) {
        return {
          ...calendarEvent,
          end: startMoment
            .clone()
            .add(15, "minutes")
            .format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        };
      }
      return calendarEvent;
    });

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointments?.pages,
    filters.eventType,
    occurrences?.pages,
    modeSelected,
  ]);

  return {
    calendarEvents,
    isFetching:
      isFetchingAppointments ||
      isFetchingOccurrences ||
      hasMoreAppointments ||
      hasMoreOccurrences,
    refetchAppointments,
  };
}
